// Open-wheel Grand Prix car at real scale (metres): 5.6 m long, 2.0 m wide,
// 3.6 m wheelbase, 720 mm tyres (305 mm front, 405 mm rear) on 18" rims.
// Generic modern single-seater: high nose onto a three-element front wing,
// sculpted sidepods with undercut, airbox and shark fin, halo, push/pull
// suspension, two-element rear wing with beam wing and rain light.
import * as THREE from 'three'
import { loft, plate, tube, wingElement } from './loft'
import { buildHelmet, type Driver } from './driver'
import type { Livery } from './livery'
import { liveryTexture, materials, mirror, numberDecal, paintMaterial, wheel } from './parts'
import type { CarModel } from './index'

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z)

const hullGeometry = loft([
  { z: -2.32, w: 0.09, h: 0.09, cy: 0.38, top: 2.2, bottom: 2.2 },
  { z: -1.95, w: 0.15, h: 0.14, cy: 0.41 },
  { z: -1.35, w: 0.23, h: 0.24, cy: 0.5 },
  { z: -0.75, w: 0.29, h: 0.33, cy: 0.6 },
  { z: -0.32, w: 0.3, h: 0.41, cy: 0.66, top: 2.0 },
  { z: 0.02, w: 0.3, h: 0.27, cy: 0.56 },
  { z: 0.55, w: 0.3, h: 0.24, cy: 0.55 },
  { z: 1.05, w: 0.25, h: 0.2, cy: 0.5 },
  { z: 1.65, w: 0.17, h: 0.14, cy: 0.42 },
  { z: 2.25, w: 0.11, h: 0.09, cy: 0.3 },
  { z: 2.66, w: 0.07, h: 0.05, cy: 0.2, top: 2, bottom: 2 },
], { steps: 80, around: 56 })

const podSections = [
  { z: -1.6, cx: 0.2, w: 0.08, h: 0.08, cy: 0.3, top: 3, bottom: 2 },
  { z: -1.0, cx: 0.36, w: 0.15, h: 0.12, cy: 0.37, top: 3.4, bottom: 1.8 },
  { z: -0.3, cx: 0.5, w: 0.21, h: 0.17, cy: 0.45, top: 4, bottom: 1.7 },
  { z: 0.3, cx: 0.55, w: 0.21, h: 0.18, cy: 0.47, top: 4.2, bottom: 1.8 },
  { z: 0.64, cx: 0.56, w: 0.2, h: 0.15, cy: 0.49, top: 5, bottom: 3 },
]
const podRight = loft(podSections, { steps: 48, around: 40 })
const podLeft = loft(podSections, { steps: 48, around: 40, mirrorX: true })

const floorGeometry = (() => {
  const shape = new THREE.Shape()
  const outline: [number, number][] = [[0.42, 1.05], [0.62, 0.7], [0.8, 0.25], [0.8, -1.25], [0.6, -1.75], [0.45, -2.0], [-0.45, -2.0], [-0.6, -1.75], [-0.8, -1.25], [-0.8, 0.25], [-0.62, 0.7], [-0.42, 1.05]]
  shape.moveTo(outline[0][0], outline[0][1])
  outline.slice(1).forEach(([x, z]) => shape.lineTo(x, z))
  const g = new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: false })
  g.rotateX(Math.PI / 2)
  g.translate(0, 0.08, 0)
  return g
})()

export const formulaPlacement = {
  hull: { numberU: -1, numberHeight: 0.8, numberSize: 0.11, nameU: 0.86, nameHeight: 0.55, nameSize: 0.09, aspect: 0.42 },
  pod: { numberU: -1, numberHeight: 0, numberSize: 0, nameU: 0.55, nameHeight: 0.62, nameSize: 0.17, aspect: 0.5 },
}

export function buildFormula(livery: Livery, driver?: Driver): CarModel {
  const car = new THREE.Group()
  const hullTex = liveryTexture(livery, formulaPlacement.hull)
  const podTex = liveryTexture(livery, formulaPlacement.pod)
  const trimTex = liveryTexture(livery, null)
  const hullPaint = paintMaterial(null, hullTex)
  const podPaint = paintMaterial(null, podTex)
  const primary = paintMaterial(livery.primary)
  const secondary = paintMaterial(livery.secondary)
  const accent = paintMaterial(livery.accent)
  const trim = paintMaterial(null, trimTex)
  const add = (g: THREE.BufferGeometry, m: THREE.Material, x = 0, y = 0, z = 0) => {
    const mesh = new THREE.Mesh(g, m)
    mesh.position.set(x, y, z)
    mesh.castShadow = true
    car.add(mesh)
    return mesh
  }

  add(hullGeometry, hullPaint)
  add(podRight, podPaint)
  add(podLeft, podPaint)
  add(floorGeometry, materials.carbon)

  // Sidepod inlets and the airbox intake.
  for (const s of [1, -1]) {
    const inlet = add(new THREE.CircleGeometry(1, 24), materials.dark, s * 0.56, 0.47, 0.625)
    inlet.scale.set(0.15, 0.085, 1)
  }
  const airbox = add(new THREE.CircleGeometry(1, 24), materials.dark, 0, 0.98, -0.12)
  airbox.scale.set(0.09, 0.07, 1)
  airbox.rotation.x = -0.15

  // Cockpit opening, headrest, driver helmet.
  const cockpit = add(new THREE.CircleGeometry(1, 32), materials.dark, 0, 0.815, 0.32)
  cockpit.scale.set(0.2, 0.42, 1)
  cockpit.rotation.x = -Math.PI / 2 + 0.06
  // The driver's own helmet (customizable in the garage).
  const helmet = buildHelmet(driver ?? {
    visor: '#141A22', helmet_pattern: 'stripe',
    helmet_primary: livery.accent, helmet_secondary: livery.secondary, suit: livery.primary, suit_secondary: livery.secondary,
    suit_accent: livery.accent, number: livery.number,
  }, 0.13)
  helmet.position.set(0, 0.92, 0.1)
  car.add(helmet)
  for (const s of [1, -1]) add(new THREE.BoxGeometry(0.06, 0.1, 0.32), materials.carbonMatte, s * 0.17, 0.84, 0.0)

  // Halo.
  add(tube([V(-0.29, 0.74, -0.22), V(-0.28, 0.93, 0.18), V(-0.15, 0.975, 0.5), V(0, 0.98, 0.58), V(0.15, 0.975, 0.5), V(0.28, 0.93, 0.18), V(0.29, 0.74, -0.22)], 0.034, 48), materials.carbon)
  add(tube([V(0, 0.98, 0.58), V(0, 0.9, 0.74), V(0, 0.78, 0.86)], 0.03), materials.carbon)

  // T-cam.
  add(new THREE.BoxGeometry(0.1, 0.05, 0.08), accent, 0, 1.095, -0.28)

  // Front wing: three cambered elements and endplates.
  add(wingElement(1.9, 0.34, 0.04, { camber: 0.05 }), primary, 0, 0.1, 2.6)
  add(wingElement(1.76, 0.22, 0.03, { camber: 0.09, tipLift: 0.05 }), secondary, 0, 0.16, 2.4)
  add(wingElement(1.6, 0.15, 0.025, { camber: 0.12, tipLift: 0.08 }), accent, 0, 0.22, 2.27)
  for (const s of [1, -1]) {
    add(plate([[2.79, 0.04], [2.79, 0.16], [2.62, 0.26], [2.26, 0.27], [2.2, 0.08], [2.34, 0.03]], 0.012), trim, s * 0.95, 0, 0)
  }
  // Nose pylons onto the wing.
  for (const s of [1, -1]) add(new THREE.BoxGeometry(0.015, 0.12, 0.3), materials.carbon, s * 0.06, 0.16, 2.5)

  // Rear wing: main plane, DRS flap, endplates, beam wing, swan-neck pylon.
  add(wingElement(1.0, 0.32, 0.05, { camber: 0.1 }), primary, 0, 0.8, -2.56)
  add(wingElement(1.0, 0.2, 0.03, { camber: 0.12 }), secondary, 0, 0.93, -2.66)
  for (const s of [1, -1]) {
    add(plate([[-2.38, 0.5], [-2.36, 0.86], [-2.46, 0.96], [-2.74, 0.97], [-2.82, 0.84], [-2.78, 0.5]], 0.014), trim, s * 0.51, 0, 0)
  }
  add(wingElement(0.86, 0.17, 0.025, { camber: 0.08 }), materials.carbon, 0, 0.36, -2.42)
  add(plate([[-2.18, 0.48], [-2.42, 0.8], [-2.62, 0.8], [-2.36, 0.48]], 0.03), materials.carbon)
  add(new THREE.BoxGeometry(0.2, 0.08, 0.04), materials.tail, 0, 0.3, -2.36)

  // Mirrors on the chassis.
  for (const s of [1, -1]) car.add(mirror(primary, V(s * 0.29, 0.77, 0.62), V(s * 0.5, 0.82, 0.6), [0.16, 0.07, 0.06]))

  // Suspension (wishbones and push/pull rods), carbon.
  const arm = (a: THREE.Vector3, b: THREE.Vector3) => add(tube([a, b], 0.017, 2), materials.carbon)
  for (const s of [1, -1]) {
    const fu = V(s * 0.68, 0.5, 1.75)
    const fl = V(s * 0.7, 0.22, 1.75)
    arm(V(s * 0.2, 0.6, 1.5), fu)
    arm(V(s * 0.19, 0.6, 2.0), fu)
    arm(V(s * 0.16, 0.3, 1.45), fl)
    arm(V(s * 0.15, 0.3, 2.05), fl)
    arm(V(s * 0.69, 0.25, 1.72), V(s * 0.2, 0.66, 1.6))
    const ru = V(s * 0.64, 0.52, -1.85)
    const rl = V(s * 0.66, 0.2, -1.85)
    arm(V(s * 0.24, 0.52, -1.55), ru)
    arm(V(s * 0.22, 0.5, -2.1), ru)
    arm(V(s * 0.22, 0.24, -1.5), rl)
    arm(V(s * 0.2, 0.24, -2.12), rl)
    arm(V(s * 0.64, 0.5, -1.82), V(s * 0.24, 0.22, -1.7))
  }

  // Wheels.
  const wheels: THREE.Group[] = []
  const band = livery.accent
  for (const [x, z, width] of [[0.8, 1.75, 0.305], [-0.8, 1.75, 0.305], [0.78, -1.85, 0.405], [-0.78, -1.85, 0.405]] as const) {
    const side = (x > 0 ? 1 : -1) as 1 | -1
    const { pivot, spin } = wheel({ radius: 0.36, width, rimRadius: 0.23, spokes: 10, band }, side)
    pivot.position.set(x, 0.36, z)
    car.add(pivot)
    wheels.push(spin)
  }

  // Number on both rear wing endplates, facing out.
  for (const s of [1, -1]) {
    const plateNumber = numberDecal(livery.number, 0.3)
    plateNumber.position.set(s * 0.525, 0.72, -2.58)
    plateNumber.rotation.y = s * Math.PI / 2
    car.add(plateNumber)
  }

  // Number on the nose.
  const nose = numberDecal(livery.number, 0.3)
  nose.position.set(0, 0.565, 1.6)
  nose.rotation.x = -Math.PI / 2 + 0.17
  car.add(nose)

  return { group: car, wheels, lamps: [], dispose: () => [hullTex, podTex, trimTex].forEach((t) => t.dispose()) }
}
